import type { Opening, Prop, Project, Space, SurfaceId } from './types'
import { SCHEMA_VERSION } from './types'
import { SURFACE_BY_ID } from './materials'
import { PROP_DEFAULTS } from './defaults'

/**
 * Upgrade a stored project (possibly from an older schema) to the current shape.
 * Tolerant by design: storage is the user's own data, so we repair rather than reject.
 *
 * v1 → v2 changes:
 *   - Prop: {type, dimensions, height} → {familyId, params}
 *   - Opening: {kind, width, height, sillHeight, swing, hinge} → {familyId, params}
 *   - BuildingSettings: add style: 'standard'
 */
export function migrateProject(raw: unknown): Project {
  const p = raw as Record<string, any>
  const levels = Array.isArray(p.levels) ? p.levels : []

  // Settings: add style if absent
  if (p.settings && !p.settings.style) {
    p.settings.style = 'standard'
  }

  for (const lvl of levels) {
    // v0 had `rooms` with `floorMaterial`; v1 uses `spaces` with the enclosure model.
    if (!Array.isArray(lvl.spaces)) {
      const rooms = Array.isArray(lvl.rooms) ? lvl.rooms : []
      lvl.spaces = rooms.map(roomToSpace)
      delete lvl.rooms
    } else {
      lvl.spaces = lvl.spaces.map(normalizeSpace)
    }

    // Props: migrate from v1 (type-based) to v2 (familyId+params)
    if (!Array.isArray(lvl.props)) {
      lvl.props = []
    } else {
      lvl.props = lvl.props.map(normalizeProp).filter(Boolean)
    }
  }

  if (p.plot && 'outdoorZones' in p.plot) delete p.plot.outdoorZones
  // Compound-wall openings (the front gate) need the same v1→v2 normalisation as space
  // openings — otherwise a migrated gate keeps its flat `kind`/`width` shape (no familyId/params)
  // and crashes the opening renderers, which read `op.params`.
  if (p.plot?.compoundWall) {
    p.plot.compoundWall.openings = Array.isArray(p.plot.compoundWall.openings)
      ? (p.plot.compoundWall.openings.map(normalizeOpening).filter(Boolean) as Opening[])
      : []
  }

  p.levels = levels
  p.schemaVersion = SCHEMA_VERSION
  return p as Project
}

function safeSurface(id: unknown): SurfaceId {
  return typeof id === 'string' && id in SURFACE_BY_ID ? (id as SurfaceId) : 'tile'
}

function roomToSpace(room: Record<string, any>): Space {
  return {
    id: room.id,
    name: room.name ?? 'Room',
    polygon: room.polygon ?? [],
    kind: 'room',
    wallMode: 'full',
    hasCeiling: true,
    surface: safeSurface(room.floorMaterial),
    floorOffset: typeof room.floorOffset === 'number' ? room.floorOffset : 0,
    ceilingHeight: room.ceilingHeight,
    labelPos: room.labelPos,
    openings: [],
    openEdges: [],
  }
}

/** Keep only valid, in-range, de-duplicated edge indices for a polygon of `n` vertices. */
function sanitizeOpenEdges(raw: unknown, n: number): number[] {
  if (!Array.isArray(raw)) return []
  return [...new Set(raw.filter((e) => Number.isInteger(e) && e >= 0 && e < n))] as number[]
}

function normalizeSpace(sp: Record<string, any>): Space {
  const polygon = sp.polygon ?? []
  return {
    id: sp.id,
    name: sp.name ?? 'Space',
    polygon,
    kind: sp.kind ?? 'room',
    wallMode: sp.wallMode ?? 'full',
    hasCeiling: sp.hasCeiling ?? true,
    surface: safeSurface(sp.surface),
    floorOffset: typeof sp.floorOffset === 'number' ? sp.floorOffset : 0,
    ceilingHeight: sp.ceilingHeight,
    labelPos: sp.labelPos,
    openings: Array.isArray(sp.openings)
      ? (sp.openings.map(normalizeOpening).filter(Boolean) as Opening[])
      : [],
    openEdges: sanitizeOpenEdges(sp.openEdges, Array.isArray(polygon) ? polygon.length : 0),
  }
}

/**
 * Normalise a stored opening (v1 or v2) to the current v2 shape.
 * v1: { kind, width, height, sillHeight, swing, hinge, edgeIndex, offset }
 * v2: { familyId, params: { width, height, sillHeight, swing, hinge, ... }, edgeIndex, offset }
 *
 * Gate kinematics defaults to 'swing' on migration (preserves existing appearance);
 * NEW gates default to 'sliding' via the gate preset.
 */
export function normalizeOpening(op: Record<string, any>): Opening | null {
  if (typeof op?.edgeIndex !== 'number' || typeof op?.offset !== 'number') return null

  // v2 format: already has familyId + params
  if (typeof op.familyId === 'string' && op.params && typeof op.params === 'object') {
    return {
      id: String(op.id),
      familyId: op.familyId,
      presetId: op.presetId,
      edgeIndex: op.edgeIndex,
      offset: op.offset,
      params: { ...op.params },
    }
  }

  // v1 format: flatten kind + fields into familyId + params
  const kind: string = op.kind === 'window' ? 'window' : op.kind === 'gate' ? 'gate' : 'door'
  const width = typeof op.width === 'number' ? op.width : kind === 'gate' ? 10 : kind === 'window' ? 4 : 3
  const height = typeof op.height === 'number' ? op.height : kind === 'gate' ? 5 : kind === 'window' ? 4 : 7
  const sillHeight = typeof op.sillHeight === 'number' ? op.sillHeight : kind === 'window' ? 3 : 0
  const swing = op.swing === 'out' ? 'out' : 'in'
  const hinge = op.hinge === 'right' ? 'right' : 'left'

  const params: Record<string, string | number | boolean> = {
    width, height, sillHeight, swing, hinge,
  }

  if (kind === 'door') {
    params.leaves = '1'
    params.panel = 'panel'
  } else if (kind === 'window') {
    params.style = 'plain'
  } else if (kind === 'gate') {
    // Existing gates migrated to 'swing' to preserve their visual appearance.
    // NEW gates placed through the UI default to 'sliding' via the preset.
    params.kinematics = 'swing'
    params.leaves = '1'
    params.style = 'simple'
  }

  return {
    id: String(op.id),
    familyId: kind,
    presetId: undefined,
    edgeIndex: op.edgeIndex,
    offset: op.offset,
    params,
  }
}

/**
 * Normalise a stored prop (v1 or v2) to the current v2 shape.
 * v1: { type, dimensions: { width, depth }, height, position, rotation, label }
 * v2: { familyId, params: { width, depth, height, ...semantics }, position, rotation, label }
 */
function normalizeProp(pr: Record<string, any>): Prop | null {
  if (!pr?.id || !pr?.position) return null

  // v2 format: already has familyId + params
  if (typeof pr.familyId === 'string' && pr.params && typeof pr.params === 'object') {
    return {
      id: String(pr.id),
      familyId: pr.familyId,
      presetId: pr.presetId,
      params: { ...pr.params },
      position: pr.position,
      rotation: typeof pr.rotation === 'number' ? pr.rotation : 0,
      label: pr.label,
    }
  }

  // v1 format: type + dimensions
  const type: string = typeof pr.type === 'string' ? pr.type : 'chair'
  // For door/window/stairs legacy props, treat as unsupported — skip them gracefully
  if (type === 'door' || type === 'window' || type === 'stairs') return null

  const defaults = PROP_DEFAULTS[type as keyof typeof PROP_DEFAULTS]
  const dim = defaults?.dim ?? { width: 2, depth: 2 }
  const defaultH = defaults?.height ?? 2

  const width = typeof pr.dimensions?.width === 'number' ? pr.dimensions.width : dim.width
  const depth = typeof pr.dimensions?.depth === 'number' ? pr.dimensions.depth : dim.depth
  const height = typeof pr.height === 'number' ? pr.height : defaultH

  // Build params from old dims; for semantic families, add sensible defaults
  const params: Record<string, string | number | boolean> = { width, depth, height }

  if (type === 'bed') {
    // Pick the closest size by width
    if (width <= 3.5) params.size = 'single'
    else if (width <= 5.5) params.size = width >= 5 ? 'double' : 'single'
    else params.size = 'king'
    params.bunk = false
    params.headboard = 'panel'
  } else if (type === 'sofa') {
    params.shape = 'straight'
    params.seatLength = width
  } else if (type === 'sink') {
    if (width <= 1.8) params.size = 'compact'
    else if (width <= 2.25) params.size = 'standard'
    else if (width <= 2.75) params.size = 'large'
    else params.size = 'garden'
  } else if (type === 'tv') {
    // Approximate diagonal from width (assuming 16:9, width ≈ diag * 0.872 / 12)
    params.diagonalIn = Math.round((width * 12) / 0.872)
    params.mount = 'stand'
  } else if (type === 'wardrobe') {
    params.doors = width >= 5 ? '3door' : '2door'
  } else if (type === 'car') {
    params.style = depth >= 15 ? 'suv' : 'hatchback'
  }

  // Map old type to the new familyId (they're identical strings for all supported types)
  return {
    id: String(pr.id),
    familyId: type,
    presetId: undefined,
    params,
    position: pr.position,
    rotation: typeof pr.rotation === 'number' ? pr.rotation : 0,
    label: pr.label,
  }
}
